// Result<T, E> — FP error handling. No exceptions for control flow.

export type Ok<T> = Readonly<{
  readonly ok: true;
  readonly value: T;
}>;

export type Err<E> = Readonly<{
  readonly ok: false;
  readonly error: E;
}>;

export type Result<T, E = string> = Ok<T> | Err<E>;

export const ok = <T>(value: T): Ok<T> => Object.freeze({ ok: true, value });

export const err = <E>(error: E): Err<E> => Object.freeze({ ok: false, error });

export const map = <T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => U,
): Result<U, E> => (result.ok ? ok(fn(result.value)) : result);

export const flatMap = <T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>,
): Result<U, E> => (result.ok ? fn(result.value) : result);

export const unwrapOr = <T, E>(result: Result<T, E>, fallback: T): T =>
  result.ok ? result.value : fallback;

export const mapErr = <T, E, F>(
  result: Result<T, E>,
  fn: (error: E) => F,
): Result<T, F> => (result.ok ? result : err(fn(result.error)));

export const fromThrowable = <T>(fn: () => T): Result<T, string> => {
  try {
    return ok(fn());
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};

export const fromPromise = async <T>(
  promise: Promise<T>,
): Promise<Result<T, string>> => {
  try {
    return ok(await promise);
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};
